import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiHelpCircle, FiX } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';
import ProCheckoutButton from './ProCheckoutButton';

const COPY = {
  en: {
    trigger: 'Compare Free and Pro',
    close: 'Close plan comparison',
    title: 'Choose how you want to search for your next job',
    free: 'Free',
    freeHeading: 'Search for jobs yourself',
    pro: 'Pro',
    proPrice: '$20/month',
    proHeading: 'Let JOBSEEQR help run your job search',
    included: 'Everything in Free, plus:',
    continueFree: 'Continue with Free',
    upgrade: 'Upgrade to Pro · $20/month',
    opening: 'Opening secure checkout…',
    freeFeatures: [
      ['Search all jobs on JOBSEEQR'],
      ['Set weekly job-search goals'],
      ['Explore possible Career Paths'],
      ['Ask the Career Coach questions', 'Free accounts include a limited number of Career Coach conversations.'],
      ['Track one company with Company Radar'],
    ],
    proFeatures: [
      ['Automated Job Scout', 'JOBSEEQR automatically finds and ranks jobs based on your CV, preferred roles and locations.'],
      ['Hiring-team outreach', 'Find relevant recruiters, create tailored emails and send automatic follow-ups.'],
      ['Tailored CVs and cover letters', 'JOBSEEQR creates a tailored CV and cover letter for every job you apply to.'],
      ['Learning Pathways', 'Build a role-specific path with real courses, practical assessments, detailed feedback and proof you can show employers.'],
      ['Interview practice — 3 sessions/month', 'Practice realistic interviews and receive feedback on your answers.'],
      ['Unlimited Company Radar', 'Research target companies, understand your fit, check role, location and work-rights signals, and follow a trackable entry plan.'],
      ['Unlimited Career Coach'],
    ],
  },
  ar: {
    trigger: 'قارن بين Free وPro',
    close: 'إغلاق مقارنة الخطط',
    title: 'اختر كيف تريد البحث عن وظيفتك القادمة',
    free: 'Free',
    freeHeading: 'ابحث عن الوظائف بنفسك',
    pro: 'Pro',
    proPrice: '20 دولاراً شهرياً',
    proHeading: 'دع JOBSEEQR يساعدك في إدارة بحثك عن عمل',
    included: 'كل ما في Free، بالإضافة إلى:',
    continueFree: 'المتابعة مع Free',
    upgrade: 'الترقية إلى Pro · 20 دولاراً شهرياً',
    opening: 'جارٍ فتح الدفع الآمن…',
    freeFeatures: [
      ['البحث في جميع وظائف JOBSEEQR'],
      ['تحديد أهداف أسبوعية للبحث عن عمل'],
      ['استكشاف المسارات المهنية الممكنة'],
      ['طرح الأسئلة على المدرب المهني', 'تتضمن الحسابات المجانية عدداً محدوداً من محادثات المدرب المهني.'],
      ['متابعة شركة واحدة في رادار الشركات'],
    ],
    proFeatures: [
      ['مساعد البحث الآلي عن الوظائف', 'يعثر JOBSEEQR تلقائياً على الوظائف ويرتبها حسب سيرتك الذاتية والوظائف والمواقع التي تفضلها.'],
      ['التواصل مع فرق التوظيف', 'اعثر على مسؤولي التوظيف المناسبين، وأنشئ رسائل مخصصة وأرسل متابعات تلقائية.'],
      ['سير ذاتية ورسائل تعريف مخصصة', 'ينشئ JOBSEEQR سيرة ذاتية ورسالة تعريف مخصصتين لكل وظيفة تتقدم إليها.'],
      ['مسارات التعلّم', 'أنشئ مساراً خاصاً بوظيفتك يتضمن دورات حقيقية وتقييمات عملية وملاحظات مفصلة ودليلاً يمكنك عرضه على أصحاب العمل.'],
      ['التدرّب على المقابلات — 3 جلسات شهرياً', 'تدرّب على مقابلات واقعية واحصل على ملاحظات حول إجاباتك.'],
      ['رادار شركات غير محدود', 'ابحث عن الشركات المستهدفة وافهم مدى ملاءمتك وتحقق من إشارات الوظيفة والموقع وحقوق العمل واتبع خطة دخول يمكنك متابعتها.'],
      ['مدرب مهني غير محدود'],
    ],
  },
};

function Feature({ item }) {
  const [label, help] = item;
  return (
    <li>
      <FiCheck aria-hidden="true" />
      <span>{label}</span>
      {help && (
        <span className="js-plan-compare__help" tabIndex="0" aria-label={`${label}: ${help}`}>
          <FiHelpCircle aria-hidden="true" />
          <span role="tooltip">{help}</span>
        </span>
      )}
    </li>
  );
}

function PlanComparison({ user, className = '', children, onCheckout, currentPlan = 'free' }) {
  const { language } = useLanguage();
  const copy = COPY[language === 'ar' ? 'ar' : 'en'];
  const [open, setOpen] = useState(false);
  const dialog = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.querySelector('button')?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key !== 'Tab') return;
      const controls = dialog.current?.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, [open]);

  return (
    <>
      <button type="button" className={className || 'js-plan-compare__trigger'} onClick={() => setOpen(true)}>
        {children || copy.trigger}
      </button>
      {open && (
        <div className="js-plan-compare__backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setOpen(false);
        }}>
          <section ref={dialog} dir={language === 'ar' ? 'rtl' : 'ltr'} className="js-plan-compare" role="dialog" aria-modal="true" aria-labelledby="plan-compare-title">
            <button type="button" className="js-plan-compare__close" onClick={() => setOpen(false)} aria-label={copy.close}>
              <FiX aria-hidden="true" />
            </button>
            <header>
              <h2 id="plan-compare-title">{copy.title}</h2>
            </header>
            <div className="js-plan-compare__plans">
              <article className="js-plan-compare__card">
                <div className="js-plan-compare__plan-name"><strong>{copy.free}</strong></div>
                <h3>{copy.freeHeading}</h3>
                <ul>{copy.freeFeatures.map((item) => <Feature key={item[0]} item={item} />)}</ul>
                <button type="button" className="js-plan-compare__free-action" onClick={() => setOpen(false)}>{currentPlan === 'pro' ? (language === 'ar' ? 'إغلاق المقارنة' : 'Close comparison') : copy.continueFree}</button>
              </article>
              <article className="js-plan-compare__card is-pro">
                <div className="js-plan-compare__plan-name"><strong>{copy.pro}</strong><span>{copy.proPrice}</span></div>
                <h3>{copy.proHeading}</h3>
                <p className="js-plan-compare__included">{copy.included}</p>
                <ul>{copy.proFeatures.map((item) => <Feature key={item[0]} item={item} />)}</ul>
                {currentPlan === 'pro' ? <button type="button" className="js-plan-compare__pro-action" onClick={() => setOpen(false)}>{language === 'ar' ? 'خطتك الحالية' : 'Your current plan'}</button> : <ProCheckoutButton user={user} onCheckout={onCheckout} className="js-plan-compare__pro-action" busyLabel={copy.opening}>
                  {copy.upgrade}
                </ProCheckoutButton>}
              </article>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default PlanComparison;
